import { useAuth0 } from '@auth0/auth0-react'
import { useConvexAuth } from 'convex/react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getDefaultPermissionsForRole, isAppRole } from '../../convex/lib/permissions'

const PROFILE_SETUP_MESSAGES = [
    'Identità Auth0 non valida: subject mancante',
    'Identità Auth0 collegata a più profili: contatta un amministratore',
    'Profilo eliminato o disabilitato: contatta un amministratore',
    'Questa email è già collegata a un’altra identità: contatta un amministratore',
    'Esistono più profili con questa email: contatta un amministratore',
    'Verifica l’indirizzo email su Auth0 prima di collegare il profilo pre-creato',
    'Inserisci un indirizzo email valido',
]

function getProfileSetupErrorMessage(error: unknown): string {
    const rawMessage = error instanceof Error ? error.message : ''
    return PROFILE_SETUP_MESSAGES.find((message) => rawMessage.includes(message)) ??
        'Non è stato possibile completare il collegamento del profilo. Riprova o contatta un amministratore.'
}

export function useAuth() {
    const { isAuthenticated, isLoading: auth0Loading, user, loginWithRedirect, logout } = useAuth0()
    const { isLoading: convexLoading, isAuthenticated: convexAuthenticated } = useConvexAuth()

    // Track if we've attempted to create a profile for this user
    const profileCreationAttempted = useRef<string | null>(null)
    const [profileSetupError, setProfileSetupError] = useState<string | null>(null)
    const [profileSetupRetry, setProfileSetupRetry] = useState(0)

    // Get current user profile
    const profile = useQuery(
        api.profiles.getCurrentProfile,
        convexAuthenticated ? {} : "skip"
    )

    // Auto-create profile mutation
    const autoCreateProfile = useMutation(api.profiles.autoCreateProfile)

    // A different Auth0 identity must start with a clean setup state.
    useEffect(() => {
        if (!isAuthenticated || profileCreationAttempted.current !== user?.sub) {
            profileCreationAttempted.current = null
            setProfileSetupError(null)
        }
    }, [isAuthenticated, user?.sub])

    // Auto-create profile if authenticated but no profile exists
    useEffect(() => {
        const shouldCreateProfile =
            isAuthenticated &&
            convexAuthenticated &&
            user &&
            profile === null && // Profile doesn't exist
            !auth0Loading &&
            !convexLoading &&
            profileCreationAttempted.current !== user.sub // Haven't tried for this user yet

        if (shouldCreateProfile) {
            profileCreationAttempted.current = user.sub!
            setProfileSetupError(null)

            autoCreateProfile()
                .then(() => {
                    setProfileSetupError(null)
                })
                .catch((error) => {
                    console.error('❌ Error auto-creating profile:', error)
                    setProfileSetupError(getProfileSetupErrorMessage(error))
                })
        }
    }, [
        isAuthenticated,
        convexAuthenticated,
        user,
        profile,
        auth0Loading,
        convexLoading,
        autoCreateProfile,
        profileSetupRetry,
    ])

    const retryProfileSetup = useCallback(() => {
        profileCreationAttempted.current = null
        setProfileSetupError(null)
        setProfileSetupRetry((attempt) => attempt + 1)
    }, [])

    const login = () => {
        loginWithRedirect({
            appState: {
                returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
            },
            authorizationParams: {
                redirect_uri: window.location.origin,
            },
        })
    }

    const logoutUser = () => {
        logout({
            logoutParams: {
                returnTo: window.location.origin,
            },
        })
    }

    const profileIsResolving = convexAuthenticated && (
        profile === undefined || (profile === null && !profileSetupError)
    )

    return {
        isLoading: auth0Loading || convexLoading || profileIsResolving,
        isAuthenticated: isAuthenticated && convexAuthenticated,
        user,
        profile,
        profileSetupError,
        retryProfileSetup,
        login,
        logout: logoutUser,
    }
}

export function usePermissions() {
    const { profile, isLoading } = useAuth()

    const hasPermission = useMemo(
        () => (permission: string): boolean => {
            if (isLoading || !profile || !profile.active) return false

            // `profile` is the effective profile returned by Convex. During
            // impersonation every UI permission must therefore match the target user.
            const { ruolo } = profile
            if (ruolo === 'amministratore') return true
            const runtimePermissions = (profile as typeof profile & {
                resolved_permissions?: string[]
            }).resolved_permissions
            if (runtimePermissions) return runtimePermissions.includes(permission)
            if (profile.extra_permissions?.includes(permission)) {
                return true
            }
            return isAppRole(ruolo)
                ? getDefaultPermissionsForRole(ruolo).includes(permission)
                : false
        },
        [isLoading, profile]
    )

    const canCreatePrescriptions = hasPermission('create_prescription')
    const canManageUsers = hasPermission('manage_users')
    const canViewCompanyUsers = hasPermission('view_company_users')
    const canViewCompanyData = hasPermission('view_company_data')
    const canManageCompanies = hasPermission('manage_companies')
    const canManageListino = hasPermission('manage_listino')
    const canViewListino = hasPermission('view_listino')
    const canViewSystemHealth = hasPermission('view_system_health')

    return {
        hasPermission,
        canCreatePrescriptions,
        canManageUsers,
        canViewCompanyUsers,
        canViewCompanyData,
        canManageCompanies,
        canManageListino,
        canViewListino,
        canViewSystemHealth,
        isLoading,
    }
}
